import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2 } from "lucide-react";

/**
 * Trustee CMS — admins can edit named "content blocks" (key + value).
 * The frontend reads /api/cms/blocks publicly. This page lets a trustee
 * override the in-code defaults without a redeploy.
 */
export default function CmsManager() {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({}); // key -> new value
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/cms");
      setItems(r.data.items || []);
      setDrafts({});
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const v = drafts[key];
    if (v == null) return;
    setBusy(key);
    try {
      await api.put(`/admin/cms/${encodeURIComponent(key)}`, { value: v });
      toast.success(`Saved "${key}"`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const reset = async (key) => {
    if (!confirm(`Reset "${key}" to the built-in default?`)) return;
    setBusy(key);
    try {
      await api.delete(`/admin/cms/${encodeURIComponent(key)}`);
      toast.success("Reset to default");
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="cms-manager">
      <div>
        <h3 className="font-display text-xl text-navy">Site content blocks</h3>
        <p className="text-xs text-slate-500 mt-1">
          Edit text used across the public site without touching the code. Changes go live
          immediately. "Reset" restores the original wording shipped with the platform.
        </p>
      </div>

      <div className="space-y-3">
        {items.map((it) => {
          const draft = drafts[it.key];
          const current = draft != null ? draft : it.current_value;
          const dirty = draft != null && draft !== it.current_value;
          const isOverride = it.is_override;
          return (
            <div key={it.key} className="bg-white border border-navy/10 rounded-sm p-4" data-testid={`cms-block-${it.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-medium text-navy text-sm">{it.label}</div>
                  <code className="text-[10px] text-slate-400">{it.key}</code>
                </div>
                <div className="text-xs text-right">
                  {isOverride ? (
                    <span className="px-2 py-0.5 bg-saffron/15 text-saffron rounded-sm">Custom</span>
                  ) : (
                    <span className="px-2 py-0.5 bg-pearl text-slate-500 rounded-sm">Default</span>
                  )}
                  {it.updated_at && (
                    <div className="text-[10px] text-slate-400 mt-1">by {it.updated_by} · {new Date(it.updated_at).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
              <textarea
                value={current}
                onChange={(e) => setDrafts((d) => ({ ...d, [it.key]: e.target.value }))}
                rows={Math.max(2, Math.ceil((current || "").length / 90))}
                data-testid={`cms-input-${it.key}`}
                className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => save(it.key)} disabled={!dirty || busy === it.key}
                  data-testid={`cms-save-${it.key}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs font-medium hover:bg-navy-light disabled:opacity-40"
                >
                  {busy === it.key ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
                {isOverride && (
                  <button onClick={() => reset(it.key)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600">
                    <RotateCcw size={12} /> Reset to default
                  </button>
                )}
                {dirty && <span className="text-xs text-saffron">Unsaved changes</span>}
              </div>
              {dirty && (
                <p className="mt-1 text-[10px] text-slate-400">Original: {it.default_value}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
